import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import './HoverReveal.scss';

interface IHoverRevealProps {
  children: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

const readBox = (el: HTMLElement | null) => {
  if (!el) return { left: 0, top: 0, width: 0 };
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.bottom, width: r.width };
};

export const HoverReveal = ({ children, content, disabled }: IHoverRevealProps) => {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(readBox(null));
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef(0);

  const clearTimer = () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = 0;
  };

  const onEnter = () => {
    if (disabled) return;
    clearTimer();
    setBox(readBox(triggerRef.current));
    setOpen(true);
  };

  const onLeave = () => {
    clearTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    if (!disabled) return;
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = 0;
    setOpen(false);
  }, [disabled]);

  useLayoutEffect(() => {
    if (!open) return;
    setBox(readBox(triggerRef.current));
    const sync = () => setBox(readBox(triggerRef.current));
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [open]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    []
  );

  const root = typeof document !== 'undefined' ? document.body : null;

  return (
    <>
      <div ref={triggerRef} className="hoverRevealTrigger" onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </div>
      {open &&
        root &&
        createPortal(
          <div
            className="hoverRevealPortal"
            style={{ left: box.left, top: box.top }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
          >
            {content}
          </div>,
          root
        )}
    </>
  );
};
